import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { connectRealtime, disconnectRealtime } from '../utils/realtimeClient';

// Helper function to extract error message from API responses
const getErrorMessage = (error, defaultMsg = 'Bir hata oluştu') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return defaultMsg;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return defaultMsg;
};

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('fortis_token'));

  // Setup axios interceptor
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('fortis_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  // Load user on mount (only if not just logged in)
  useEffect(() => {
    const loadUser = async () => {
      const savedToken = localStorage.getItem('fortis_token');
      const savedUser = localStorage.getItem('fortis_user');
      
      // If we have saved user data, use it instead of fetching from /me
      // This preserves the branch_id selected during login
      if (savedToken && savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          setToken(savedToken);
          connectRealtime();
        } catch (error) {
          console.error('Failed to parse saved user:', error);
          localStorage.removeItem('fortis_token');
          localStorage.removeItem('fortis_user');
          setToken(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (username, password, remember = false, branchId = null) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password, branch_id: branchId });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('fortis_token', access_token);
      localStorage.setItem('fortis_user', JSON.stringify(userData));
      if (remember) {
        localStorage.setItem('fortis_remember', 'true');
      }
      
      setToken(access_token);
      setUser(userData);
      toast.success('Giriş başarılı!');
      connectRealtime();
      return { success: true, user: userData };
    } catch (error) {
      const message = getErrorMessage(error, 'Giriş başarısız');
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (email, password, name, role) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        name,
        role
      });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('fortis_token', access_token);
      setToken(access_token);
      setUser(userData);
      toast.success('Kayıt başarılı!');
      return { success: true };
    } catch (error) {
      const message = getErrorMessage(error, 'Kayıt başarısız');
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('fortis_token');
    localStorage.removeItem('fortis_user');
    localStorage.removeItem('fortis_remember');
    // Clear all info banner dismissals on logout
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('info_banner_dismissed_')) {
        sessionStorage.removeItem(key);
      }
    });
    setToken(null);
    setUser(null);
    disconnectRealtime();
    toast.success('Çıkış yapıldı');
  };

  // Refresh user data from API (useful for panel_access updates)
  const refreshUser = async () => {
    const savedToken = localStorage.getItem('fortis_token');
    if (!savedToken) return null;
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      setUser(userData);
      localStorage.setItem('fortis_user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
